import { FC, useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CmsPage, CmsPageVersion } from "services/cms-seo/type"

import { formatDateTime } from "./helpers/format"
import { ghostButtonClass, modalBoxClass, primaryButtonClass} from "./CmsUi"

interface VersionHistoryModalProps {
  open: boolean
  pageId: string
  onClose: () => void
  onRestored: (page: CmsPage) => void
}

export const VersionHistoryModal: FC<VersionHistoryModalProps> = ({ open, pageId, onClose, onRestored }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [versions, setVersions] = useState<CmsPageVersion[]>([])
  const [loading, setLoading] = useState(false)
  const [restoring, setRestoring] = useState<number | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setVersions(await cmsSeoService.listVersions(pageId))
    setLoading(false)
  }, [pageId])

  useEffect(() => {
    if (open) load()
  }, [open, load])

  const restore = async (version: number) => {
    setRestoring(version)
    const restored = await cmsSeoService.restoreVersion(pageId, version)
    setRestoring(null)
    if (restored) {
      toast.success(t("cmsSeo.versionRestored", { defaultValue: "Version restored as a draft" }) as string)
      onRestored(restored)
      onClose()
    }
  }

  return (
    <Modal open={open} handler={onClose}>
      <div className={`${modalBoxClass} max-w-xl`}>
        <h3 className="mb-4 text-lg font-semibold">{t("cmsSeo.history", { defaultValue: "History" })}</h3>

        {loading ? (
          <p className="py-8 text-center text-sm">{t("common.loading", { defaultValue: "Loading..." })}</p>
        ) : versions.length === 0 ? (
          <p className="py-8 text-center text-sm opacity-70">
            {t("cmsSeo.noVersions", { defaultValue: "No earlier versions — this page has not been published yet." })}
          </p>
        ) : (
          <ul className="max-h-[420px] overflow-y-auto">
            {versions.map((version) => (
              <li
                key={version.version}
                className="flex items-center gap-3 border-b border-black/[0.06] py-2.5 last:border-b-0 dark:border-white/[0.06]"
              >
                <div className="flex-1">
                  <div className="text-sm font-medium">v{version.version}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {formatDateTime(version.created_at)}
                    {version.created_by ? ` · ${version.created_by}` : ""}
                    {version.note ? ` · ${version.note}` : ""}
                  </div>
                </div>
                <button
                  type="button"
                  className={primaryButtonClass}
                  disabled={restoring === version.version}
                  onClick={() => restore(version.version)}
                >
                  {t("cmsSeo.restore", { defaultValue: "Restore" })}
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="modal-action flex-wrap">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.close", { defaultValue: "Close" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
